import { BoxNetworkItem, BoxWifiItem } from '@/box/interface'
import { getFormatMessage } from '@/utils/utils'
import { Button, Col, Form, Input, Radio, Row, Select } from 'antd'
import React, { useEffect, useState } from 'react'
import { Title } from '../../shared/Title'

export interface BoxDetailNetWifiProps {
  wifiItems?: BoxWifiItem[]
  editItem?: BoxNetworkItem
  title?: boolean
  disabled?: boolean
  onSave?: (item: BoxNetworkItem) => void
  onWifiScan?: () => Promise<void>
}

export const BoxDetailNetWifi: React.FC<BoxDetailNetWifiProps> = (
  props: BoxDetailNetWifiProps
) => {
  const { wifiItems, disabled, title, editItem, onSave, onWifiScan } = props
  const formatMessage = getFormatMessage()
  const [wifiForm] = Form.useForm<BoxNetworkItem>()
  const [ipSource, setIpSource] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [ssid, setSsid] = useState<string>('')
  const [scanLoading, setScanLoading] = useState<boolean>(false)
  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 5 }
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 18 }
    }
  }
  const labelCol4 = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 4 }
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 19 }
    }
  }

  const handleWifiSave = async () => {
    if (!onSave) return
    try {
      const value = await wifiForm.validateFields()
      if (!value.extra) value.extra = {}
      value.extra.ssid = ssid
      value.networkType = 4
      setLoading(true)
      await onSave(value)
    } finally {
      setLoading(false)
    }
  }

  const handleWifiScan = async () => {
    try {
      setScanLoading(true)
      await onWifiScan?.()
    } finally {
      setScanLoading(false)
    }
  }

  useEffect(() => {
    if (editItem) {
      wifiForm.setFieldsValue(editItem)
      setIpSource(editItem.ipSource || 0)
      setSsid(editItem.extra?.ssid || '')
    } else {
      wifiForm.setFieldsValue({
        ipSource: 0,
        ipAddr: undefined,
        subnetMask: undefined,
        gateway: undefined,
        dns1: undefined,
        dns2: undefined,
        extra: {
          ssid: undefined,
          password: undefined
        }
      })
      setSsid('')
    }
  }, [editItem])

  return (
    <Form {...formItemLayout} form={wifiForm} labelAlign='right'>
      {title && (
        <Title
          level={4}
          extra={
            <Button
              type='primary'
              disabled={disabled}
              loading={loading}
              onClick={handleWifiSave}
            >
              {formatMessage({ id: 'app.box.detail.edit.save' })}
            </Button>
          }
        >
          {formatMessage({ id: 'app.box.detail.edit.wifi' })}
        </Title>
      )}
      <Row>
        <Col span={24}>
          <Form.Item name='ipSource'>
            <Radio.Group
              // disabled={disabled}
              // always disabled
              disabled={true}
              onChange={(e) => setIpSource(e.target.value)}
            >
              <Radio value={0}>
                {formatMessage({ id: 'app.box.ipSource.0' })}
              </Radio>
              <Radio value={1}>
                {formatMessage({ id: 'app.box.ipSource.1' })}
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={8}>
          <Form.Item
            {...labelCol4}
            label='SSID'
            rules={[
              {
                required: true,
                message: 'SSID不能为空'
              }
            ]}
          >
            <Input.Group compact>
              <Select
                style={{ width: '60%' }}
                disabled={disabled || scanLoading}
                getPopupContainer={(trigger) => trigger.parentNode}
                value={ssid}
                onChange={(e) => setSsid(e)}
              >
                {wifiItems
                  ?.filter((x) => x.apName !== '')
                  .map((x, i) => {
                    return (
                      <Select.Option key={`ssid_${i}`} value={x.apName!}>
                        {x.apName}
                      </Select.Option>
                    )
                  })}
              </Select>
              <Button
                type='primary'
                className='ant-input-search-button'
                style={{ width: '40%' }}
                disabled={disabled}
                onClick={handleWifiScan}
                loading={scanLoading}
              >
                {formatMessage({ id: 'app.box.detail.edit.scan' })}
              </Button>
            </Input.Group>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name={['extra', 'password']}
            label={formatMessage({ id: 'app.box.detail.edit.password' })}
            rules={[
              {
                required: true,
                message: '密码不能为空'
              }
            ]}
          >
            <Input.Password disabled={disabled} />
          </Form.Item>
        </Col>
        <Col span={8}></Col>
      </Row>
      <Row>
        <Col span={8}>
          <Form.Item
            {...labelCol4}
            name='ipAddr'
            label={formatMessage({ id: 'app.box.detail.edit.ipAddr' })}
            rules={
              ipSource === 0
                ? []
                : [
                    {
                      required: true,
                      message: 'IP地址不能为空'
                    }
                  ]
            }
          >
            <Input disabled={disabled || ipSource === 0} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name='subnetMask'
            label={formatMessage({ id: 'app.box.detail.edit.subnetMask' })}
            rules={
              ipSource === 0
                ? []
                : [
                    {
                      required: true,
                      message: '子网掩码不能为空'
                    }
                  ]
            }
          >
            <Input disabled={disabled || ipSource === 0} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name='gateway'
            label={formatMessage({ id: 'app.box.detail.edit.gateway' })}
            rules={
              ipSource === 0
                ? []
                : [
                    {
                      required: true,
                      message: '网关不能为空'
                    }
                  ]
            }
          >
            <Input disabled={disabled || ipSource === 0} />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={8}>
          <Form.Item
            {...labelCol4}
            name='dns1'
            label='DNS1'
            rules={
              ipSource === 0
                ? []
                : [
                    {
                      required: true,
                      message: 'DNS1不能为空'
                    }
                  ]
            }
          >
            <Input disabled={disabled || ipSource === 0} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name='dns2' label='DNS2'>
            <Input disabled={disabled || ipSource === 0} />
          </Form.Item>
        </Col>
        <Col span={8}>
          {/* <Form.Item label='MAC'>
            <Input disabled={disabled || ipSource === 0} />
          </Form.Item> */}
        </Col>
      </Row>
    </Form>
  )
}
